import { DatePipe } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import {
  CertificateRecord,
  EventType,
  PublicEvent,
  compareSequence,
} from "../transparency-log";
import {
  WatchContext,
  describeEvent,
  eventLink,
  replacementNote,
} from "../watch-view-model";
import { CertificateInspector } from "./certificate-dialog.component";

type EventFilter = "all" | EventType;

const EVENT_ICONS: Record<string, string> = {
  certificate_issued: "add_moderator",
  certificate_revoked: "remove_moderator",
  registration_recorded: "how_to_reg",
};

const PAGE = 50;

@Component({
  selector: "watch-activity-view",
  imports: [DatePipe, MatIconModule],
  templateUrl: "./activity-view.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivityViewComponent {
  private readonly inspector = inject(CertificateInspector);

  readonly events = input.required<PublicEvent[]>();
  readonly context = input.required<WatchContext>();
  readonly openWrp = output<string>();

  readonly filter = signal<EventFilter>("all");
  readonly limit = signal(PAGE);
  readonly filters: Array<{ value: EventFilter; label: string }> = [
    { value: "all", label: "All events" },
    { value: "certificate_issued", label: "Issued" },
    { value: "certificate_revoked", label: "Revoked" },
    { value: "registration_recorded", label: "Registration" },
  ];

  readonly matching = computed(() =>
    this.events()
      .filter(
        (event) =>
          this.filter() === "all" || event.event_type === this.filter(),
      )
      .sort((left, right) =>
        compareSequence(right.sequence_number, left.sequence_number),
      ),
  );

  /** Newest first, grouped by publication day. */
  readonly days = computed(() => {
    const groups: Array<{ day: string; events: PublicEvent[] }> = [];
    for (const event of this.matching().slice(0, this.limit())) {
      const day = event.published_at.slice(0, 10);
      const last = groups[groups.length - 1];
      if (last?.day === day) {
        last.events.push(event);
      } else {
        groups.push({ day, events: [event] });
      }
    }
    return groups;
  });

  setFilter(filter: EventFilter): void {
    this.filter.set(filter);
    this.limit.set(PAGE);
  }

  showMore(): void {
    this.limit.update((limit) => limit + PAGE);
  }

  describe(event: PublicEvent) {
    const info = describeEvent(this.context(), event);
    const record = info.record;
    return {
      ...info,
      icon: EVENT_ICONS[event.event_type] ?? "help_outline",
      // On a revocation, only the successor is of interest.
      replacement:
        record &&
        (event.event_type === "certificate_issued"
          ? record.replaces
          : record.replacedBy)
          ? replacementNote(this.context(), record)
          : undefined,
      name: this.context().names.get(event.wrp_id) ?? event.wrp_id,
    };
  }

  eventLink(event: PublicEvent): string {
    return eventLink(this.context(), event);
  }

  inspect(record: CertificateRecord): void {
    this.inspector.open(record, this.context());
  }
}
